import { z } from 'zod';
import { dateOnlySchema, uuidSchema } from './common';
import type { WaybillWarning } from './waybill-task-rows';

// ── Периоды назначения: история машины и машиниста внутри срока заказа ──
// (план `docs/assignment-periods-plan.md`, этап 1)
//
// Сегодня у заказа спецтехники ровно одно назначение на весь срок: машина, ставки и машинист лежат
// денормализованной строкой, и «кто работал в марте» портал восстанавливает по бумаге. Разрез
// (Р3–Р6) заменяет это историей изменений: у каждой шкалы — `vehicle` и `driver` — своя строка со
// своей датой вступления в силу, а состояние на дату считает свёртка. Отсюда всё остальное:
// недельный лист ЭСМ-2 перестаёт быть единицей планирования (Р5), а его состав задают отрезки
// истории, а не одна пара «машина + человек» из карточки.
//
// **Этот файл — словарь модуля и тела его дверей.** Здесь нет ни свёртки, ни планировщика бумаги:
// свёртка (`assignmentStateOn`, `assignmentEffectRange`) живёт в `assignment-history.ts`, план
// листов (`esm2SheetPlan`, `documentClosure`) — в своём модуле. Формы, которые они возвращают и
// принимают, названы здесь один раз: `DriverState` обязан быть одной формой в таблице, в свёртке, в
// DTO и в плане (Р19) — свернись он где-нибудь обратно в `null`, план перестал бы отличать «бланк
// ведёт арендодатель» от «историю не восстановили».
//
// Двери появляются на этапе 3, портал — на этапах 4a и 6. Схемы выписаны раньше намеренно: у этой
// фичи пять дверей, и рукопожатие у них общее (версия, отпечаток предпросмотра, подтверждения
// предупреждений, envelope журнала). Разойдись они в деталях — расходились бы и последствия.

// ── Словарь модели (§6 плана: DDL) ──
// Значения перечислений здесь дословно те же, что в CHECK'ах `vehicle_request_assignment_changes`:
// схема, свёртка и база обязаны понимать одно и то же слово одинаково, иначе строка, прошедшая
// валидацию, упрётся в ограничение таблицы уже внутри транзакции.

/**
 * Шкалы истории. Их две, и они независимы **на уровне строк** (Р3): у одной даты законно стоят два
 * изменения разного происхождения — сменили машину и тем же днём назначили другого машиниста.
 *
 * «Не менялось» выражается отсутствием строки, а не значением: `NULL` у шкалы `driver` — это
 * осознанное «машиниста снять» (арендный отрезок), а у `vehicle` он запрещён вовсе.
 */
export const ASSIGNMENT_DIMENSIONS = ['vehicle', 'driver'] as const;
export const assignmentDimensionSchema = z.enum(ASSIGNMENT_DIMENSIONS);
export type AssignmentDimension = (typeof ASSIGNMENT_DIMENSIONS)[number];

export const assignmentDimensionLabels: Record<AssignmentDimension, string> = {
  vehicle: 'Техника',
  driver: 'Машинист',
};

/**
 * Состояния шкалы машиниста (Р19). Три, а не два, и третье — не «ещё не заполнили»:
 *
 * - `set` — машинист назначен, лист выписывается на него;
 * - `cleared` — снят осознанно: отрезок арендный, бланк ведёт арендодатель;
 * - `unknown` — историю восстановить нечем. Пишет его только бэкфилл (`origin = 'backfill'`) и
 *   производный остаток частичного заполнения (`origin = 'unknown_remainder'`); человек такого
 *   состояния не заводит никогда — он либо называет машиниста, либо снимает его.
 *
 * Разница между `cleared` и `unknown` бумажная, а не косметическая: на заблокированном прошлом
 * `unknown` означает «лист выдан, человек напечатан в нём, а история этого дня приблизительна», и
 * сверка считает такой лист совпавшим. Свернись `unknown` в `cleared`, сверка каждый раз хотела бы
 * переоформить старую бумагу, кем именно — не зная.
 */
export const DRIVER_STATE_KINDS = ['set', 'cleared', 'unknown'] as const;
export type DriverStateKind = (typeof DRIVER_STATE_KINDS)[number];

/**
 * Состояние шкалы машиниста одной формой — дискриминированным union, а не парой
 * «`personId: string | null` + флаг».
 *
 * Пара допускает четвёртое сочетание, которого не существует (`unknown` с названным человеком), и
 * молча теряет различие при каждом преобразовании между слоями. Union проходит сквозь таблицу,
 * свёртку, DTO и план в неизменном виде — ровно этого требует Р19.
 */
export const driverStateSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('set'), personId: uuidSchema }).strict(),
  z.object({ state: z.literal('cleared') }).strict(),
  z.object({ state: z.literal('unknown') }).strict(),
]);
export type DriverState = z.infer<typeof driverStateSchema>;

/**
 * Происхождение строки истории — тот же список, что в CHECK таблицы.
 *
 * Провенанс здесь не украшение журнала, а признак, по которому команды находят свои строки:
 *
 * - `assignment` — начальное изменение, рождённое переводом заявки в работу; не отменяется никогда;
 * - `reassignment` — обычная смена техники существующей дверью;
 * - `machinist_change` — смена машиниста, в том числе якорем составной команды;
 * - `backfill` — восстановление истории по бумаге; единственный источник `unknown` без коррекции;
 * - `tail_resolution` — решение расхождения хвоста (Р31): vehicle-граница и её driver-спутник;
 * - `known_fill` — заполнение `unknown` известным человеком (Р29, Ф1);
 * - `unknown_remainder` — граница `unknown` за концом заполненного отрезка (Ш4).
 *
 * Последние два заведены отдельными словами именно ради отмены (Ю2): по составу строк группу
 * заполнения не отличить от обычной исторической смены машиниста, и отмена «по составу» превратила
 * бы **известного** человека обратно в `unknown` — то есть стёрла бы знание, которого никто не
 * отменял.
 */
export const ASSIGNMENT_CHANGE_ORIGINS = [
  'assignment',
  'reassignment',
  'machinist_change',
  'backfill',
  'tail_resolution',
  'known_fill',
  'unknown_remainder',
] as const;
export const assignmentChangeOriginSchema = z.enum(ASSIGNMENT_CHANGE_ORIGINS);
export type AssignmentChangeOrigin = (typeof ASSIGNMENT_CHANGE_ORIGINS)[number];

/**
 * Снимок авторизации операции (Р9): что требовалось, когда её разрешили.
 *
 * Повтор спустя недели проверяет **сохранённые требования** — «есть ли это право у субъекта
 * сейчас», — а глубину и архивный статус заново не считает: операция, бывшая моложе тридцати дней
 * при первом вызове, к повтору успевает состариться, и пересчёт отказал бы в том, что уже сделано.
 *
 * Состав — булевы требования, а не исходные данные для их пересчёта. `archive.read` в списке нет
 * намеренно: дверь ремонта адресуется идентификатором и архивную область не проходит (Ц3), так что
 * требовать это право не может ни одна операция истории.
 */
export const correctionAuthorizationScopeSchema = z
  .object({
    /* Не украшение: снимок читается спустя недели, и без версии первая же эволюция состава сломала
     * бы повторы уже записанных операций. */
    schemaVersion: z.literal(1),
    requiresCorrect: z.boolean(),
    requiresCorrectBeyondLimit: z.boolean(),
    requiresArchiveRestore: z.boolean(),
    /** `correctionEffectiveDate` — самая ранняя дата мутаций команды (Р11), та же, по которой
     * считалась глубина. */
    effectiveDate: dateOnlySchema,
    /** Календарный ключ дня, в который права проверялись. */
    authorizedAsOf: dateOnlySchema,
  })
  .strict();
export type WaybillCorrectionAuthorizationScope = z.infer<
  typeof correctionAuthorizationScopeSchema
>;

/**
 * Как строка перестала быть актуальной. Строки неизменяемы (Р3): правка помечает прежнюю
 * `replaced` и вставляет новую, отмена помечает `cancelled` и замены не имеет.
 *
 * Погашенные строки не оживают — поэтому отмена заполнения не «гасит и всё», а иногда обязана
 * вставить новую границу `unknown` (Щ2): иначе через дыру протянулось бы состояние, действовавшее
 * **до** неё, и отмена дописала бы историю, которой никто не заявлял.
 */
export const ASSIGNMENT_SUPERSEDE_KINDS = ['replaced', 'cancelled'] as const;
export type AssignmentSupersedeKind = (typeof ASSIGNMENT_SUPERSEDE_KINDS)[number];

/**
 * Готовность истории заявки (Р20, Р26) — три значения, а не булев `ready`.
 *
 * `ready = false` отвечало сразу на два вопроса — «истории нет» и «история есть, но невалидна», — и
 * второй бэкфилл по такой заявке ловил бы UNIQUE поверх уже записанных строк.
 */
export const ASSIGNMENT_HISTORY_STATES = ['empty', 'materialized', 'ready'] as const;
export type AssignmentHistoryState = (typeof ASSIGNMENT_HISTORY_STATES)[number];

/**
 * Исход команды (Р32) — **один** вычисляемый признак, из которого выводится всё остальное:
 * `needsOperation = outcome !== 'none'`, `needsCorrection = outcome === 'crew'`.
 *
 * Два независимых признака уже разъезжались дважды. Порядок старшинства
 * `crew > assignment_tail > none` тоже нормативен: строки матрицы пересекаются (историческая замена
 * подходит сразу под две), поэтому исход — максимум по логическим эффектам команды, а не первое
 * совпавшее условие.
 *
 * - `none` — новое изменение датой `asOf` или позже: причины не спрашивают вовсе;
 * - `assignment_tail` — команда правит уже принятое решение или заводит дремлющую запись прошлой
 *   датой: причина обязательна, коррекционных прав не нужно — прошлого она не трогает;
 * - `crew` — мутация задевает непустой исторический диапазон: причина, `waybills.correct`, а
 *   глубже тридцати дней — `correctBeyondLimit`.
 */
export const ASSIGNMENT_OPERATION_OUTCOMES = ['none', 'assignment_tail', 'crew'] as const;
export type AssignmentOperationOutcome = (typeof ASSIGNMENT_OPERATION_OUTCOMES)[number];

// ── Общие кирпичи тел (§7 плана) ──

/**
 * Отпечаток: `sha256` от каноникализованного содержимого, посчитанный сервером и возвращаемый ему
 * нетронутым. Их в модуле четыре — предпросмотра, множества разблокировок, снимаемых часов и
 * погашаемых групп, — и все они одной формы: портал их не разбирает, а только носит обратно.
 *
 * `.max(128)` — не про базу, а про мусор: отпечаток длиннее шестидесяти четырёх символов hex не
 * бывает, и тело с килобайтом в этом поле означает ошибку клиента, а не длинный хеш.
 */
export const assignmentFingerprintSchema = z.string().trim().min(1).max(128);

/** Версия заявки: оптимистическая блокировка, как у всех её дверей. */
const versionSchema = z.number().int().nonnegative();

/**
 * Envelope журнала (§7) — **общий верхний уровень каждой боевой команды** модуля: и команды
 * истории, и ремонта, и смены техники, и правки срока.
 *
 * Почему снаружи, а не внутри коррекционного блока: первичный `history_wins` (Р31) коррекцией не
 * является вовсе — у него нет ни цели, ни исторической строки, он лишь переводит назначение на
 * машину, которую история и так знает, — а причину положить ему куда-то надо. Так же устроены
 * замена будущего решения и дремлющая запись прошлой датой: журнал им нужен, коррекция — нет.
 *
 * Обязателен **семантически**, а не схемой: спрашивать причину решает `operationOutcome` (Р32),
 * который считается под блокировкой, а из тела его не видно. Схема, потребовавшая envelope у всех,
 * отклоняла бы плановую смену машиниста с понедельника — обычную работу диспетчера, у которой
 * причины нет и быть не должно.
 */
export const operationInputSchema = z
  .object({
    /**
     * Ключ идемпотентности (Р9): повтор после обрыва связи возвращает прежний результат, а не жжёт
     * второй номер строгой отчётности. Версия заявки от этого не спасает — она ответит 409 там, где
     * человек ждёт «уже сохранено».
     */
    operationId: uuidSchema,
    /**
     * Причина операции. Непустая: `waybill_corrections.reason` — `NOT NULL` с CHECK на непустоту,
     * то есть операция без объяснения физически невозможна.
     */
    reason: z.string().trim().min(1, 'Укажите причину').max(2000),
  })
  .strict();
export type OperationInput = z.infer<typeof operationInputSchema>;

/**
 * Якорь машиниста (Р16): дополнительное `driver`-изменение ровно на той границе, где свёртка
 * осталась бы без человека.
 *
 * Заявка в якоре **не называется**: якоря принимают только однозаявочные двери (Р22), и заявка
 * всегда та, что в пути запроса. Появись здесь `requestId` — поле стало бы второй дверью в чужую
 * историю, минуя права и предпросмотр той заявки.
 *
 * Портал человека не подставляет никогда (ADR 0083), в том числе «того, кто работал до аренды»:
 * подставленная фамилия уезжает в бланк строгой отчётности настоящей.
 */
export const machinistAnchorSchema = z
  .object({
    effectiveDate: dateOnlySchema,
    driverPersonId: uuidSchema,
  })
  .strict();
export type MachinistAnchor = z.infer<typeof machinistAnchorSchema>;

/**
 * Набор якорей одной команды.
 *
 * Жёсткого предела на длину нет намеренно (Б3): историческая коррекция способна атомарно
 * потребовать больше дюжины якорей, а записать их вне этой же команды нельзя — промежуточное
 * состояние нарушит инвариант Р16. Границей остаётся размер тела запроса (413), и это единственная
 * граница, которая не делает корректную команду невыполнимой.
 *
 * Даты уникальны: два якоря на один день — это два разных человека на одной дате, то есть
 * противоречие внутри одного тела. Разбирать его на сервере поздно и незачем.
 *
 * Пустой список отвергается: он ничего не сообщает, а от «поля нет» отличается только формой.
 */
export const machinistAnchorsSchema = z
  .array(machinistAnchorSchema)
  .min(1, 'Пустой список якорей ничего не сообщает')
  .refine((v) => new Set(v.map((a) => a.effectiveDate)).size === v.length, {
    message: 'Два якоря на одну дату: машинист на день может быть только один',
  });

/**
 * Разрешение расхождения хвоста (Р31): машина, действующая на конце срока, не равна машине
 * назначения, и продление срока заперто, пока человек не скажет, какая из них права.
 *
 * - `assignment_wins` — «дальше работает машина назначения»: пишется дремлющая vehicle-граница на
 *   `dateTo + 1`; ставки не трогаются, они уже её;
 * - `history_wins` — «назначение записано ошибочно»: назначение и ставки переводятся на машину
 *   истории, а **исторического изменения не пишется вовсе** — история эту машину уже знает.
 *
 * Ставки есть только у второй ветки, и это не экономия полей: `assignment_wins` меняет историю, а
 * не назначение, и ставка в его теле означала бы правку цены под видом решения о хвосте. Требуются
 * они по тем же правилам, что у смены техники (у арендной машины — хотя бы одна), и проверяет это
 * сервер: принадлежность машины видна ему, а не схеме.
 */
export const tailResolutionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('assignment_wins') }).strict(),
  z
    .object({
      kind: z.literal('history_wins'),
      pricePerHour: z.coerce.number().positive().nullish(),
      pricePerShift: z.coerce.number().positive().nullish(),
      shiftHours: z.coerce.number().int().min(1).max(24).nullish(),
    })
    .strict(),
]);
export type TailResolution = z.infer<typeof tailResolutionSchema>;

/**
 * Заполнение `unknown` известным человеком (Р29, Ф1) — **отрезок**, а не перечень дней.
 *
 * Дыры `unknown` тянутся месяцами: заполнение день за днём раздуло бы тело до тысяч элементов ради
 * данных, которые по смыслу и есть отрезок («с января по март работал Иванов»).
 *
 * Отрезок обязан целиком лежать внутри `unknown`-промежутка, названного предпросмотром (чужая
 * граница — 422), но покрывать его целиком не обязан: половину истории восстанавливают сейчас,
 * половину — когда найдут документы. Правило «всё или ничего» отвергнуто по причине сильнее
 * простоты — оно толкает знающего половину вписать на вторую половину правдоподобное, а `unknown`
 * тем и ценен, что отличает «не знаем» от «знаем».
 */
export const knownFillSchema = z
  .object({
    from: dateOnlySchema,
    to: dateOnlySchema,
    personId: uuidSchema,
  })
  .strict()
  .refine((v) => v.to >= v.from, {
    message: 'Конец отрезка раньше начала',
    path: ['to'],
  });
export type KnownFill = z.infer<typeof knownFillSchema>;

/**
 * Набор заполняемых отрезков одной команды.
 *
 * Пересечения отвергает схема, а не сервер: два отрезка на один день — это два разных человека на
 * одной дате, и разрешить их значило бы записать историю, зависящую от порядка обхода массива.
 * Границы `unknown`-промежутков схеме неизвестны — их знает только предпросмотр, — поэтому здесь
 * проверяется ровно то, что видно самому телу.
 */
export const knownFillsSchema = z
  .array(knownFillSchema)
  .min(1, 'Пустой список заполнений ничего не сообщает')
  .superRefine((v, ctx) => {
    const sorted = [...v].sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      const cur = sorted[i]!;
      if (cur.from <= prev.to) {
        ctx.addIssue({
          code: 'custom',
          message: `Отрезки заполнения пересекаются: ${prev.from}–${prev.to} и ${cur.from}–${cur.to}`,
        });
        return;
      }
    }
  });

/**
 * Рукопожатие по **каждому** выпускаемому листу (Б4): `issueKey → отпечаток предупреждений`.
 *
 * Одного `acknowledge` на команду не хватает с тех пор, как разрез перестал быть недельным: одна
 * команда выпускает несколько листов с разными людьми и разными пробелами в их документах, и один
 * отпечаток не может подтвердить несколько разных наборов. Человек должен видеть, что именно он
 * подтверждает по каждому бланку строгой отчётности.
 *
 * Ключ — индекс элемента в отсортированном плане (`issueKey`), но записанный **строкой**: ключи
 * объекта JSON строками и бывают. Канон записи один — десятичная запись без ведущих нулей, — иначе
 * `'01'` и `'1'` подтвердили бы один и тот же лист дважды, и сервер не смог бы сказать, какое из
 * двух подтверждений человек видел.
 *
 * Записи требуются только для листов с **непустым** набором предупреждений: чистый бланк
 * подтверждать нечем, и требовать пустое рукопожатие значило бы просить подпись под пустотой.
 */
export const assignmentAcknowledgementsSchema = z.record(
  z.string().regex(/^(0|[1-9]\d*)$/, 'Ключ листа — его номер в плане'),
  assignmentFingerprintSchema,
);

/**
 * Does a planned sheet need a signature (B4)? Only when its warning set is non-empty.
 *
 * ONE CARRIER. The server demands signatures by this rule and rejects a signature for a sheet it
 * does not hold (`assertAssignmentIssueAcknowledgements`); the portal windows decide by it which
 * sheets to show for confirmation and which signatures to send. Two copies of the filter would
 * drift silently, and the first sign would be a door refusing a signature the window was sure it
 * had to send.
 *
 * Two shapes of the same set are accepted: the full `warnings` of the dispatcher's preview and the
 * anonymized `codes` of the early-end approver's (R26). `codes` is the set of the warnings' codes,
 * so it is non-empty exactly when the warnings are.
 */
export function assignmentIssueNeedsAcknowledgement(
  issue: { warnings: readonly unknown[] } | { codes: readonly unknown[] },
): boolean {
  return ('warnings' in issue ? issue.warnings : issue.codes).length > 0;
}

/**
 * The signatures a command carries for the preview the person confirmed: one per sheet that needs
 * one, under the canonical key (decimal `issueKey`, no leading zeros — what `String` of an integer
 * gives). An empty object means nothing to sign; the doors do not want the field at all then.
 */
export function assignmentAcknowledgementsOf(
  issues: readonly ({ issueKey: number; warningFingerprint: string } & (
    { warnings: readonly unknown[] } | { codes: readonly unknown[] }
  ))[],
): Record<string, string> {
  return Object.fromEntries(
    issues
      .filter(assignmentIssueNeedsAcknowledgement)
      .map((issue) => [String(issue.issueKey), issue.warningFingerprint]),
  );
}

/**
 * Цель команды над существующим изменением (Р10) — адресуется двумя способами, и оба нужны.
 *
 * `changeId` — для готовой истории и для портала, который её уже прочитал. Логический ключ
 * `{ dimension, effectiveDate }` — не удобство, а единственный доступный адрес у заявки, история
 * которой ещё не материализована: строк там нет вовсе, предпросмотр не коммитит (Р20), и
 * `changeId` появился бы только внутри будущего apply — то есть исправить восстановленную из листов
 * смену машины было бы нечем.
 *
 * Однозначность логического ключа держит частичный UNIQUE `(request_id, dimension,
 * effective_date)` по актуальным строкам. Принимается только актуальная строка: заменённая
 * описывает уже отменённое решение.
 */
export const assignmentChangeTargetSchema = z.union([
  z.object({ changeId: uuidSchema }).strict(),
  z
    .object({
      dimension: assignmentDimensionSchema,
      effectiveDate: dateOnlySchema,
    })
    .strict(),
]);
export type AssignmentChangeTarget = z.infer<typeof assignmentChangeTargetSchema>;

// ── Дверь истории: `POST /vehicle-requests/:id/assignment-changes` (+ `/preview`) ──

/**
 * Команда истории — две формы, а не три (Р13): заведение/правка (`set`) и отмена (`cancel`).
 *
 * Третьей формы — отделения строки от группы — нет намеренно (Р31): зависимая строка живёт из-за
 * своей vehicle-границы, и отделённая она пережила бы отмену группы, оставив собственный отрезок
 * без человека или с чужим. Это нарушение Р16, рождённое операцией «просто поменять метку».
 *
 * `set` меняет **только машиниста** — отсюда `dimension: 'driver'` литералом. Машину меняют
 * существующими дверями (Р7): сегодняшнюю — окном смены техники, прошлую — коррекцией с целью, и
 * заводить ей вторую дорогу через эту дверь значило бы держать два расходящихся набора правил про
 * ставки, аренду и рейс.
 *
 * `unlockFingerprint` нужен именно здесь, а не только у коррекции: исторический `set` или `cancel`
 * получает исход `crew` и переоформляет отработанные недели, а Р11 требует их подтвердить. Списком
 * идентификаторов это не выражается — заказ живёт годами, и предел в 53 листа делал бы законную
 * команду невыполнимой (Б3), — а отпечатком серверного множества выражается точно.
 */
const assignmentCommandCommonShape = {
  /**
   * Якоря машиниста (Р16), названные первым проходом предпросмотра. Принимается только та дата,
   * которую он назвал; чужая — 422.
   */
  anchors: machinistAnchorsSchema.optional(),
  /** Отпечаток серверного множества листов под разблокировку; спрашивается при исходе `crew`. */
  unlockFingerprint: assignmentFingerprintSchema.optional(),
  /** Подтверждения предупреждений — по одному на лист с непустым набором (Б4). */
  acknowledgements: assignmentAcknowledgementsSchema.optional(),
  version: versionSchema,
  /**
   * Отпечаток последствий, показанных предпросмотром.
   *
   * **Необязателен в схеме и спрашивается сервером** — тем же приёмом, что у смены статуса заявки.
   * Причин две. Предпросмотр двухфазный (Р16): первый его вызов идёт без якорей, отпечатка ещё не
   * существует — он его и вычисляет, — а тело у предпросмотра и боевой ручки одно (§8). И
   * обязателен отпечаток не всегда одинаково: у **любой** непустой команды последствий, включая ту,
   * у которой бумажный план пуст (дремлющее изменение, Р24), — а пуста команда или нет, видно
   * только после расчёта под блокировкой. Схема, потребовавшая отпечаток у всех, отвечала бы `400`
   * там, где сервер обязан ответить понятным `409` с новыми последствиями.
   *
   * Это же поле объясняет, зачем оно вообще: между просмотром и нажатием план меняется, не тронув
   * заявку, — чужая команда заняла ту же дату, лист аннулировали своей ручкой, наступила полночь.
   * `version` заявки ни одного из этих случаев не ловит.
   */
  previewFingerprint: assignmentFingerprintSchema.optional(),
  /** Envelope журнала; спрашивает его `operationOutcome`, а не календарь (Р32). */
  operation: operationInputSchema.optional(),
};

export const setAssignmentChangeSchema = z
  .object({
    ...assignmentCommandCommonShape,
    kind: z.literal('set'),
    dimension: z.literal('driver'),
    /**
     * Дата вступления в силу. Раньше начала срока — 422: там нет работы. Позже `dateTo`
     * допускается (Р24) и вступает в силу, если срок продлят: без этого ремонт истории оказывался
     * недостижим — площадка не может продлить заявку, у которой в новых днях нет машиниста, а
     * назначить его заранее было негде.
     */
    effectiveDate: dateOnlySchema,
    /**
     * Кого назначают. `null` здесь не принимается: осознанное снятие машиниста (`cleared`) —
     * спутник vehicle-изменения на арендную машину, и пишет его сервер той же операцией и той же
     * датой (Р16). Отдельная команда «снять машиниста» на собственном отрезке нарушила бы
     * инвариант «у `portal`-отрезка машинист есть всегда» — тем же 422, только на круг позже.
     */
    driverPersonId: uuidSchema,
  })
  .strict();

export const cancelAssignmentChangeSchema = z
  .object({
    ...assignmentCommandCommonShape,
    kind: z.literal('cancel'),
    /**
     * Что отменяем. Гасится всегда вся `change_group_id`, а не одна строка: погасив vehicle-границу
     * и оставив её `cleared`-спутника, мы получили бы отрезок «собственная машина без машиниста» —
     * либо 422 на Р16, либо молчаливое наследование чужого человека следующим отрезком.
     *
     * Отмена — не «удаление строки»: она способна аннулировать два отрезка и выписать объединённый
     * лист новым номером, то есть тратит бланки так же, как заведение, и рукопожатие ей нужно то же.
     */
    target: assignmentChangeTargetSchema,
  })
  .strict();

export const assignmentCommandSchema = z.discriminatedUnion('kind', [
  setAssignmentChangeSchema,
  cancelAssignmentChangeSchema,
]);
export type AssignmentCommandInput = z.infer<typeof assignmentCommandSchema>;
export type AssignmentCommandBody = z.input<typeof assignmentCommandSchema>;

/**
 * Тело предпросмотра — то же самое, что у боевой команды (§8).
 *
 * Схема одна намеренно, тем же приёмом, что у предпросмотра смены статуса: предпросмотр обязан
 * считать последствия по тем входам, по которым их потом исполнит боевая ручка. Своя схема
 * разошлась бы с боевой на первом же новом поле, и окно начало бы обещать не то.
 */
export const assignmentCommandPreviewSchema = assignmentCommandSchema;
export type AssignmentCommandPreviewInput = AssignmentCommandInput;

// ── Дверь смены техники: `PATCH /vehicle-requests/:id/assignment` (+ `/preview`) ──

/**
 * Коррекционный блок смены техники (Р10) — **только предметные поля**: что правим.
 *
 * Причина и ключ операции отсюда ушли наверх, в общий `operation`: они нужны и тем командам,
 * которые коррекцией не являются. Списка разблокировок здесь нет вовсе (В1) — он серверный, и
 * полем HTTP не бывает; тело подтверждает его отпечатком.
 */
export const assignmentCorrectionBlockSchema = z
  .object({ target: assignmentChangeTargetSchema })
  .strict();
export type AssignmentCorrectionBlock = z.infer<typeof assignmentCorrectionBlockSchema>;

/**
 * Что периоды назначения добавляют телу смены техники (§7).
 *
 * Почему блок, а не целая схема: `changeVehicleAssignmentSchema` уже существует в
 * `vehicle-requests.ts` — машина, ставки, рейс, версия, — и второй экспорт того же имени сделал бы
 * `export *` из `index.ts` неоднозначным (TS2308; прецедент с `hasModuleWideScope` описан там же).
 * На этапе 3 существующая схема расширяется этим блоком, и расширение остаётся одним объектом, а не
 * россыпью полей, которую следующая дверь скопирует не целиком.
 *
 * Якоря нужны прежде всего **исторической коррекции машины**, а она идёт именно этой дверью (Р7):
 * без них тест «коррекция январского `own → rental` с якорем проходит» нечем выразить по HTTP.
 */
export const changeVehicleAssignmentExtrasSchema = z
  .object({
    anchors: machinistAnchorsSchema.optional(),
    /** Отпечаток последствий; необязателен в схеме по той же причине, что и у команды истории. */
    previewFingerprint: assignmentFingerprintSchema.optional(),
    unlockFingerprint: assignmentFingerprintSchema.optional(),
    /**
     * Подтверждение снимаемых часов (Ц2). Смена машины удаляет **заполненные без подписи** часы
     * своего диапазона — объект вводит их заново по новой машине, — и обязателен этот отпечаток
     * ровно тогда, когда серверное множество непусто. Подписанные дни удалению не подлежат: они
     * запирают диапазон и снимаются только коррекцией.
     *
     * Два множества и два поля, а не одно: «из-за чего команда невозможна» и «что будет удалено,
     * если подтвердишь» — разные вопросы, и один список отвечал бы на них одинаково неверно.
     */
    clearedShiftsFingerprint: assignmentFingerprintSchema.optional(),
    acknowledgements: assignmentAcknowledgementsSchema.optional(),
    operation: operationInputSchema.optional(),
    correction: assignmentCorrectionBlockSchema.optional(),
  })
  .strict();
export type ChangeVehicleAssignmentExtras = z.infer<typeof changeVehicleAssignmentExtrasSchema>;

// ── Дверь периодной коррекции: `POST /vehicle-requests/:id/assignment-changes/correction` ──

/**
 * Периодная коррекция — правка **прошлого решения о машине**, адресуемая целью (Р7, Р10, Р11).
 *
 * ЧТО ЭТО ЗА ДВЕРЬ. До истории назначение было одно на весь срок, и «в марте работала другая
 * машина» выражалось единственным способом — переписать назначение целиком (`PATCH /:id/assignment`
 * с блоком коррекции). С разрезом у заявки появляются отрезки, и правится **один** из них: цель
 * называется явно, последствия считаются по её диапазону, подписи снимаются ровно в нём.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ:
 *
 * - **шкалы `driver`** — прошлое решение о человеке правит дверь машиниста той же датой
 *   (`assignmentCommandSchema`, `kind: 'set'`): там уже есть якоря Р16 и правила `cleared`, и
 *   вторая дорога к тому же решению означала бы два расходящихся набора правил;
 * - **отмены** — гашение vehicle-изменения не восстановило бы ставки (Р13): история их не хранит, а
 *   `saveAssignment` перезаписал их безвозвратно. Машину «откатывают» новым решением;
 * - **ставок, аренды и рейса** — их правит окно смены техники (Р7). Поэтому коррекция принимает
 *   только машину той же принадлежности и не трогает хвост истории: сменить машину, которой заявка
 *   закрыта **сейчас**, значит переписать и назначение со ставками, а это другая дверь.
 */
export const assignmentVehicleCorrectionSchema = z
  .object({
    /** Что правим (Р10): идентификатор изменения либо логический ключ «шкала + дата». */
    target: assignmentChangeTargetSchema,
    /** Какая машина работала на самом деле. */
    vehicleId: uuidSchema,
    version: versionSchema,
    /**
     * Отпечаток последствий, показанных предпросмотром. Необязателен в схеме и спрашивается
     * сервером — по той же причине, что у команды истории: пуста команда или нет, видно только
     * после расчёта под блокировкой.
     */
    previewFingerprint: assignmentFingerprintSchema.optional(),
    /** Отпечаток серверного множества листов под разблокировку; спрашивается при исходе `crew`. */
    unlockFingerprint: assignmentFingerprintSchema.optional(),
    /**
     * Подтверждения предупреждений — по одному на выпускаемый лист с непустым набором (Б4).
     *
     * У этой двери они нужны ровно затем же, зачем у соседних: после переключения чтения коррекция
     * переоформляет задетые отрезки сама, и бланк с пробелами в документах машиниста человек
     * обязан подтвердить по каждому листу отдельно. Схема объявлена `.strict()` — без этого поля
     * подтверждение отвергалось бы валидацией **до** обработчика, то есть окно не смогло бы его
     * прислать вовсе.
     */
    acknowledgements: assignmentAcknowledgementsSchema.optional(),
    /** Envelope журнала; спрашивает его `operationOutcome`, а не календарь (Р32). */
    operation: operationInputSchema.optional(),
  })
  .strict();
export type AssignmentVehicleCorrectionInput = z.infer<typeof assignmentVehicleCorrectionSchema>;
export type AssignmentVehicleCorrectionBody = z.input<typeof assignmentVehicleCorrectionSchema>;

/**
 * Тело предпросмотра — то же самое, что у боевой ручки (§8): расчёт обязан идти по тем входам, по
 * которым его потом исполнит боевая ручка.
 */
export const assignmentVehicleCorrectionPreviewSchema = assignmentVehicleCorrectionSchema;

/** Подпись объекта, которую коррекция снимет: день, кто принял часы и когда (Р5, ADR 0101 п. 16). */
export interface AssignmentClearedApprovalDto {
  date: string;
  approvedByName: string | null;
  approvedAt: string;
}

/**
 * Предпросмотр периодной коррекции — общий ответ плюс то, что есть только у неё.
 *
 * Подписи показываются **списком дней**, а не числом: объект расписался под конкретными сутками, и
 * «снимется 4 подписи» не даёт человеку проверить, те ли это дни, которые он правит.
 */
export interface AssignmentCorrectionPreviewDto extends AssignmentPreviewDto {
  /**
   * Действующие листы ЭСМ-2, которых команда касается, — то, что **запирает** её до этапа 5
   * (паритет старой бумаги, Б1/В3). От `requiredUnlocks` отделены нарочно: там перечень бумаги,
   * которую операция переоформит, здесь — перечень бумаги, из-за которой операции не будет, и
   * свести их в один список значило бы обещать переоформление, которого не случится.
   */
  blockingSheets: AssignmentUnlockDto[];
  /** Диапазон снятия подписей (`approvalClearRange`, Р11) — только дни изменённой машины. */
  approvalClearRange: { from: string; to: string }[];
  /**
   * Sign-offs the command will clear: approved days inside the range that did not sit in a day
   * route (ADR 0210 — a route day's vehicle is the route's, and the route correction owns it).
   */
  clearedApprovals: AssignmentClearedApprovalDto[];
}

// ── Дверь ремонта истории: `POST /vehicle-requests/:id/assignment-changes/repair` ──

/**
 * Общая часть обеих веток ремонта: рукопожатие и режим.
 *
 * `restore` — не работа, а режим: он выводит заявку из архива **вместе с ремонтом**, одной
 * транзакцией, чтобы не осталось состояния «историю починили, а архив не сняли». Само по себе
 * восстановление делает своя ручка (`POST /vehicle-requests/:id/restore`), и дублировать её здесь
 * незачем.
 */
const repairCommonShape = {
  /**
   * Снять архив той же транзакцией (Р29). Запрошен явно — архив снимается всегда, и post-restore
   * сверка выполняется даже при пустом плане: у дремлющего `assignment_wins` он как раз обычно
   * пуст. Требует права `archive.restore`.
   *
   * У архивной заявки решение хвоста без него запрещено вовсе (Р31): иначе ремонт создал бы
   * дремлющую группу, не сняв архива, а править и отменять её нужно общей дверью, у которой
   * архивной области нет и заводить её незачем.
   */
  restore: z.boolean().optional(),
  unlockFingerprint: assignmentFingerprintSchema.optional(),
  acknowledgements: assignmentAcknowledgementsSchema.optional(),
  version: versionSchema,
  /** Отпечаток последствий; необязателен в схеме по той же причине, что и у команды истории. */
  previewFingerprint: assignmentFingerprintSchema.optional(),
  operation: operationInputSchema.optional(),
};

/**
 * Ремонт: команда **восстанавливает** то, чего история не знает.
 *
 * Работ у двери три, и все они здесь: пробелы машиниста (`anchors`), расхождение хвоста
 * (`tailResolution`, Р31) и заполнение `unknown` известным человеком (`knownFills`, Р29).
 */
export const repairWorkSchema = z
  .object({
    ...repairCommonShape,
    mode: z.literal('repair'),
    anchors: machinistAnchorsSchema.optional(),
    /**
     * Заполнение `unknown` на **заблокированных** днях: история старых заявок доводится до полной.
     *
     * Поле выкатывается отдельным последним релизом (подэтап 6a), после ответа бухгалтерии: там,
     * где листа за эти дни не было вовсе, сверка **выпишет недостающие бланки задним числом**, и
     * расход строгой отчётности реальный. До ответа сервер отвечает `409
     * backdated_issue_not_authorized`, а портал поля не показывает — запрет механический, а не
     * договорённость. В схеме поле при этом есть: без него строгая валидация отклонила бы запрос
     * раньше, чем он дошёл бы до предусмотренного отказа, и человек прочёл бы «лишнее поле» вместо
     * «выписка бланков задним числом не согласована» (Ц4).
     */
    knownFills: knownFillsSchema.optional(),
    tailResolution: tailResolutionSchema.optional(),
  })
  .strict();
export type RepairWorkInput = z.infer<typeof repairWorkSchema>;

/**
 * Отмена заполнения: команда **снимает** ранее заявленное утверждение о факте.
 *
 * Дверь та же, что заполнила: та, что умеет заводить, обязана уметь и снимать. Но команда другая —
 * и живёт она отдельной веткой, а не необязательным полем общей схемы (Ю1). Слейся ветки в одну,
 * `target` можно было бы прислать вместе с якорями, заполнениями и решением хвоста, и смысл команды
 * («восстанавливаю» против «снимаю») пришлось бы выяснять по составу тела — то есть по тому, что
 * человек **не** прислал.
 *
 * Общая часть у веток одна: архивная заявка, версия, отпечаток и рукопожатия нужны отмене ровно
 * так же.
 */
export const cancelFillSchema = z
  .object({
    ...repairCommonShape,
    mode: z.literal('cancel_fill'),
    /**
     * Группа заполнения (Р13, Э2). Отменяемую группу определяет **провенанс, а не состав**: ровно
     * одна актуальная строка `known_fill` плюс не более одной `unknown_remainder`. Группа, не
     * отвечающая этому описанию, отменяется обычным `cancel`, а не как заполнение (422
     * `not_a_known_fill_group`) — иначе отмена превратила бы известного человека в `unknown`.
     */
    target: z.object({ changeGroupId: uuidSchema }).strict(),
  })
  .strict();
export type CancelFillInput = z.infer<typeof cancelFillSchema>;

/**
 * Тело двери ремонта: две взаимоисключающие ветки с дискриминантом `mode` (Ю1).
 *
 * Требование «хотя бы одна работа» стоит на союзе, а не внутри ветки: `z.discriminatedUnion`
 * разбирает объекты по литералу, и ветка, обёрнутая в `superRefine`, перестала бы им быть.
 *
 * Пустой ремонт отвергается схемой, потому что отвергать его больше негде: `restore` работой не
 * является (у него своя ручка), а команда без якорей, заполнений и решения хвоста дошла бы до
 * сервера, прошла бы все проверки и записала бы операцию журнала, ничего не изменив, — то есть
 * оставила бы в журнале коррекций строку с причиной и без предмета.
 */
export const repairSchema = z
  .discriminatedUnion('mode', [repairWorkSchema, cancelFillSchema])
  .superRefine((v, ctx) => {
    if (v.mode !== 'repair') return;
    if (!v.anchors && !v.knownFills && !v.tailResolution) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Ремонт без работы: назовите машинистов на пробелах, заполнение или решение по хвосту',
      });
    }
  });
export type RepairInput = z.infer<typeof repairSchema>;
export type RepairBody = z.input<typeof repairSchema>;

// ── Дверь срока: `POST /vehicle-requests/:id/period/preview` и `PATCH /:id/period` ──

/**
 * Правка срока — своя дверь (Ж4, З5), а не поля широкого `PATCH /:id`.
 *
 * Узкое тело туда не легло бы: `updateVehicleRequestSchema` — строгий union с обязательными типом
 * заявки, техникой, заказчиком, контактами и файлами, и «продлить срок» пришлось бы оформлять как
 * полную правку заявки. Смешанная команда «продлить и заодно поправить комментарий» распадается на
 * две — у них разные последствия и разные рукопожатия.
 */
export const periodCommandSchema = z
  .object({
    version: versionSchema,
    dateFrom: dateOnlySchema.optional(),
    /**
     * `null` — «срок открыт»: заказ на объект живёт без даты окончания, пока его не завершат. Это
     * не «поле не прислали» — то выражается отсутствием ключа.
     */
    dateTo: dateOnlySchema.nullable().optional(),
  })
  .strict();
export type PeriodCommand = z.infer<typeof periodCommandSchema>;

/**
 * Предпросмотр срока знает меньше боевой команды — и это не упущение (Л1): отпечатка, исхода и
 * ключей выпускаемых листов у него ещё нет, он их и вычисляет. Одинакова у двух тел семантическая
 * часть, а не тело целиком.
 */
export const periodPreviewSchema = periodCommandSchema;
export type PeriodPreviewInput = PeriodCommand;

export const periodApplySchema = periodCommandSchema
  .extend({
    /*
     * Необязателен **в схеме**, обязателен на сервере — как у команды машиниста и коррекции.
     * Разница видна человеку: отсутствие отпечатка это 409 «посмотрите последствия заново», а не
     * 400 «поле обязательно». Первое объясняет, что делать; второе выглядит поломкой клиента.
     * Требование само по себе никуда не девается — его держит шаг 7 канона.
     */
    previewFingerprint: assignmentFingerprintSchema.optional(),
    /**
     * Подтверждение перечня групп, которые гасит **сокращение** срока (Д2). Гашение здесь не
     * побочный эффект правки `dateTo`, а изменение истории: оставленная за новым концом срока
     * vehicle-строка ожила бы при следующем продлении — без решения о ставках и занятости, в обход
     * Р7.
     */
    cancelGroupsFingerprint: assignmentFingerprintSchema.optional(),
    unlockFingerprint: assignmentFingerprintSchema.optional(),
    acknowledgements: assignmentAcknowledgementsSchema.optional(),
    operation: operationInputSchema.optional(),
  })
  .strict();
export type PeriodApplyInput = z.infer<typeof periodApplySchema>;
export type PeriodApplyBody = z.input<typeof periodApplySchema>;

// ── Ответы предпросмотра (§7) ──

/**
 * Пробел машиниста, который человек обязан закрыть якорем.
 *
 * Номер заказа здесь нужен, хотя якорь его не называет: тот же тип возвращают **чужие** двери в
 * отказе (Р22), а недельная операция называет сразу несколько заказов — и «назначьте машиниста с
 * 1 марта» без номера заявки не подсказка, а загадка.
 */
export interface RequiredAnchor {
  requestId: string;
  requestNumber: string;
  /** Дата, на которую ждут якорь; только её и примет команда. */
  effectiveDate: string;
  /** Отрезок без машиниста целиком — им окно объясняет, за какие дни спрашивает. */
  from: string;
  to: string;
}

/**
 * Расхождение хвоста (Р31): машина, действующая на конце срока, против машины назначения.
 *
 * Обе названы **и идентификатором, и именем**: идентификатор нужен команде, имя — человеку, а
 * второй запрос за именами из окна отказа означал бы, что портал показывает выбор не сразу.
 */
export interface RequiredVehicleResolution {
  tailVehicleId: string;
  tailVehicleName: string;
  assignmentVehicleId: string;
  assignmentVehicleName: string;
  /** С какого дня расхождение начинает действовать — `dateTo + 1`. */
  since: string;
}

/**
 * Нужна ли операция журнала, какого вида и что для неё спросить (Р32).
 *
 * По нему окно решает, показывать ли поля причины и ключа, — а **не по календарю**: отмена
 * дремлющего решения с прошлогодней датой причину требует, а плановая смена машиниста на будущее —
 * нет. Считать это на клиенте по дате означало бы держать вторую редакцию матрицы Р32, которая
 * разойдётся с серверной на первом же уточнении.
 *
 * `null` — исход `none`: причины не спрашивают вовсе.
 */
export interface OperationRequirement {
  kind: Exclude<AssignmentOperationOutcome, 'none'>;
  /**
   * Оба признака выписаны явно, хотя сегодня оба истинны при любом непустом исходе: окно не должно
   * выводить их из `kind` — в тот день, когда правило разойдётся, разойдётся молча и портал.
   */
  reasonRequired: boolean;
  operationIdRequired: boolean;
}

/** День работы с часами: цена подтверждения должна быть видна, а не подразумеваться. */
export interface AssignmentShiftDay {
  date: string;
  hours: number;
}

/** Лист под разблокировку — номером и неделей: ими окно называет человеку бумагу, о которой спрашивает. */
export interface AssignmentUnlockDto {
  waybillId: string;
  displayNumber: string;
  from: string;
  to: string;
}

/** Аннулируемый лист плана: номер человеку, период — чтобы он узнал свою неделю. */
export interface AssignmentPlanCancelDto {
  waybillId: string;
  displayNumber: string;
  from: string;
  to: string;
}

/**
 * Выпускаемый лист плана — с составом, а не одними границами.
 *
 * До разреза состав добирался из глобальной пары «машина + машинист» заявки, и этого хватало: на
 * календарную неделю приходился один лист. После Р5 пн–вт обязаны выйти как `A + Иван`, а ср–вс —
 * как `B + Пётр`, и восстановить это по ключу недели нечем.
 */
export interface AssignmentPlanIssueDto {
  /**
   * Стабильный ключ, посчитанный **до** записи: индекс элемента в плане, отсортированном по
   * `(from, to, vehicleId, driverPersonId)`. Сортировать по `id` нельзя вовсе — он появится только
   * после расхода номера, а сгенерированные идентификаторы в отпечаток предпросмотра не входят.
   */
  issueKey: number;
  from: string;
  to: string;
  vehicleId: string;
  vehicleName: string;
  driverPersonId: string;
  driverName: string;
}

/** Предупреждения одного выпускаемого листа и их отпечаток — рукопожатие строится по ним (В1). */
export interface AssignmentIssueWarningsDto {
  issueKey: number;
  warnings: WaybillWarning[];
  /**
   * `sha256` от каноникализованных **фактов**, а не от текста: подтверждает человек положение дел,
   * а не формулировку на экране. Его же портал возвращает ключом `issueKey` в `acknowledgements`.
   */
  warningFingerprint: string;
}

/**
 * Общий ответ предпросмотров модуля: что случится, если нажать.
 *
 * Один DTO на пять дверей намеренно. Окно последствий у них одно и то же — сгорающие и
 * выписываемые листы, пробелы машиниста, снимаемые часы, разблокировки, — и своя форма у каждой
 * двери означала бы пять окон, расходящихся по мере правок.
 */
export interface AssignmentPreviewDto {
  /** Бумажный план: что сгорит и что выпишется. Пустой план — законный ответ (дремлющее изменение, Р24). */
  plan: {
    cancel: AssignmentPlanCancelDto[];
    issue: AssignmentPlanIssueDto[];
  };
  /**
   * Пробелы машиниста, которые обязана закрыть та же команда (Р16). Непусты — предпросмотр
   * **первой фазы**: набор последствий ещё неизвестен, подтверждать нечего, и окно спрашивает имена.
   */
  requiredAnchors: RequiredAnchor[];
  /** Расхождение хвоста, запирающее продление (Р31); `null` — хвост согласован. */
  requiredVehicleResolution: RequiredVehicleResolution | null;
  /**
   * Дни, **из-за которых команда невозможна**: часы подписаны объектом, и снимаются они только
   * коррекцией. Смешанный диапазон — 422, и ни одна строка не удаляется.
   */
  blockedShiftDays: AssignmentShiftDay[];
  /**
   * Дни, которые **будут удалены**, если человек подтвердит: часы внесены, но не подписаны, а после
   * смены машины они относились бы к технике, которой в этот день не было. Часы показываются,
   * чтобы объект понимал цену подтверждения.
   *
   * Список отдельный от `blockedShiftDays`, а не флаг внутри одного: «нельзя» и «будет стёрто» —
   * разные ответы, и слитые в один они дали бы окно, которое либо пугает лишним, либо молчит о
   * потере.
   */
  clearedShiftDays: AssignmentShiftDay[];
  /** Отпечаток `clearedShiftDays`; `null` — снимать нечего, и подтверждать нечего. */
  clearedShiftsFingerprint: string | null;
  /** Листы под разблокировку — для показа (Р11). Команда носит не их, а отпечаток. */
  requiredUnlocks: AssignmentUnlockDto[];
  /** Отпечаток серверного множества разблокировок; `null` — множество пусто. */
  unlockFingerprint: string | null;
  /** Предупреждения по каждому выпускаемому листу: из них окно строит рукопожатия (Б4). */
  issues: AssignmentIssueWarningsDto[];
  /** Нужна ли операция журнала и что для неё спросить (Р32); `null` — исход `none`. */
  operationRequirement: OperationRequirement | null;
  /**
   * Календарный день, на который посчитаны последствия (`asOf`, Р32). Он входит в отпечаток, и
   * переход бизнес-даты обесценивает предпросмотр **всегда** — даже когда исход не изменился:
   * предпросмотр 19 августа с командой 20-го просто не сойдётся. Человек увидит 409 и посмотрит
   * последствия заново.
   */
  asOf: string;
  /**
   * Отпечаток последствий. Возвращается боевой ручке полем `previewFingerprint`: между просмотром и
   * нажатием план меняется, не тронув заявку, — чужая команда заняла ту же дату, лист аннулировали
   * своей ручкой, наступила полночь. `version` заявки ни одного из этих случаев не ловит.
   */
  fingerprint: string;
}

/**
 * Группа истории, которую гасит сокращение срока: даты, шкалы и состав (Д2).
 *
 * Состав показывается целиком, потому что гашение групповое: человек, сокращающий срок, должен
 * увидеть, что вместе с майской машиной уходит и её майский машинист.
 */
export interface CancelledAssignmentGroupDto {
  changeGroupId: string;
  rows: {
    effectiveDate: string;
    dimension: AssignmentDimension;
    /** Машина изменения; `null` — строка шкалы `driver`. */
    vehicle: { vehicleId: string; name: string } | null;
    /** Состояние машиниста; `null` — строка шкалы `vehicle`. */
    driver: DriverState | null;
    origin: AssignmentChangeOrigin;
  }[];
}

/** Ответ предпросмотра срока: общие последствия плюс то, что есть только у него. */
export interface PeriodPreviewDto extends AssignmentPreviewDto {
  cancelGroups: CancelledAssignmentGroupDto[];
  /** Отпечаток перечня погашаемых групп; `null` — сокращения нет, гасить нечего. */
  cancelGroupsFingerprint: string | null;
}

/**
 * Ответ двери ремонта — и предпросмотра, и осмотра (`GET .../repair/state`, подэтап 6a).
 *
 * Форма одна на оба запроса нарочно: окно рисует одним компонентом и «что чинить», и «что будет,
 * если нажать». У осмотра работы в теле нет вовсе, поэтому `stateAfter` равен `state`, а план
 * бумаги пуст — это ответ, а не обещание.
 */
export interface RepairPreviewDto extends AssignmentPreviewDto {
  /** Состояние истории **сейчас** (Р26): `materialized` — чинить есть что. */
  state: AssignmentHistoryState;
  /** Каким станет состояние, если нажать (Р27). У осмотра равно `state`. */
  stateAfter: AssignmentHistoryState;
  /** Блокеры до команды интервалами — проекция для карточки, а не единица сравнения. */
  blockedDays: { from: string; to: string }[];
  /**
   * Промежутки `unknown` на **заблокированных** днях — единственные адреса `knownFills` (Ц4).
   *
   * На изменяемых днях та же дыра чинится якорями, и второго способа назвать человека там нет.
   * Отличить одно от другого может только сервер: это зависит от отменяемости бумаги.
   */
  fillableGaps: { from: string; to: string }[];
  /** Заявка в архиве: дверь открыла её по идентификатору (Ц3). */
  archived: boolean;
  /** Пуст ли бумажный план для гипотетического `deleted_at = null` (Р29). */
  paperFree: boolean;
  /** Архивной заявке с непустым планом ремонт разрешён только режимом `restore` (Р29). */
  restoreRequired: boolean;
}

/** Итог боевой ручки ремонта: чем кончилось и в каком состоянии осталась история. */
export interface RepairResultDto {
  ok: true;
  repeated: boolean;
  version: number;
  state: AssignmentHistoryState;
  operationId: string | null;
  archived: boolean;
}

// ── История заявки: `GET /vehicle-requests/:id/assignment-changes` ──

/**
 * Одно изменение истории глазами портала.
 *
 * `id` необязателен намеренно: до материализации история существует только расчётом (Р20) —
 * предпросмотр её не коммитит, — и у расчётных строк идентификаторов нет. Адресовать их можно
 * логическим ключом `{ dimension, effectiveDate }`, ради чего он в цели команды и появился.
 */
export interface AssignmentChangeDto {
  id: string | null;
  effectiveDate: string;
  dimension: AssignmentDimension;
  /** Машина изменения; `null` — строка шкалы `driver`. */
  vehicle: { vehicleId: string; name: string } | null;
  /** Состояние машиниста; `null` — строка шкалы `vehicle`. */
  driver: DriverState | null;
  origin: AssignmentChangeOrigin;
  /** Группа решения: гашение и отмена работают ею, а не отдельной строкой (Р31). */
  changeGroupId: string;
  /** Операция журнала, породившая строку; `null` — обычная работа без коррекции. */
  correctionId: string | null;
  createdAt: string;
  createdByName: string | null;
  /**
   * Как строка перестала быть актуальной; `null` — действует. Погашенные строки показываются
   * наравне с актуальными: журнал заявки читают, чтобы понять, **что** правили, а не только чем
   * дело кончилось.
   */
  supersededKind: AssignmentSupersedeKind | null;
  supersededAt: string | null;
  supersededByName: string | null;
}

/**
 * История назначения заявки целиком — то, из чего портал строит «Состав по датам».
 *
 * Состояние готовности отдаётся вместе с изменениями, а не отдельной ручкой: окно смены машиниста
 * обязано отличить «истории нет» от «история есть, но невалидна», и второй запрос за этим означал
 * бы, что между ними состояние успевает измениться.
 */
/**
 * Человек, названный в истории назначения: имя и, если карточку сняли, день снятия
 * (план `machinist-card-removal`, Э4).
 *
 * Имена приходят ВМЕСТЕ с историей, а не разрешаются порталом по справочнику водителей, и это не
 * удобство, а единственный способ вообще их показать. Справочник снятые карточки прячет — и
 * «Состав по датам» у заявки, где машиниста как раз и сняли, писал бы «машиниста нет в справочнике
 * водителей» ровно там, где человек работал и на его имя выписаны бланки.
 *
 * Отбор машинистов для ВЫБОРА этим не трогается: он так и предлагает только годных. Здесь —
 * обратная задача: назвать того, кто уже назван.
 */
export interface AssignmentPersonDto {
  personId: string;
  fullName: string;
  /** День снятия карточки; `null` — карточка жива. */
  cardRemovedOn: string | null;
}

export interface RequestAssignmentHistoryDto {
  state: AssignmentHistoryState;
  /**
   * День, на который состояние считалось. Отличается от `asOf`: календарь меняет валидность сам,
   * без всякой двери, — поэтому состояние пересчитывается лениво, под блокировкой заявки, прежде
   * чем на него сошлётся любое решение.
   */
  validatedOn: string | null;
  /**
   * Загрязнение внутри дня (К4): ручки смен меняют отменяемость бумаги, а `validatedOn` при этом
   * остаётся сегодняшним, и ленивое правило устаревания молчало бы.
   */
  dirty: boolean;
  changes: AssignmentChangeDto[];
  /**
   * Люди, названные в `changes`, — по одному на идентификатор. Пустой список законен: у заявки,
   * которую ведут по-старому, решений с человеком может не быть вовсе.
   */
  people: AssignmentPersonDto[];
}
